<template>
    <div class="wrapper auth-split w-100 h-100">
        <div class="auth-split__photo" aria-hidden="true"></div>
        <div class="auth-split__panel">
        <v-card class="auth-split__card auth-split__card--wide" elevation="0">
            <v-card-title>
                <router-link class="d-flex justify-center align-center text-decoration-none" to="/">
                    <div class="text-black d-flex flex-column align-center">
                        <v-img :src="logo" :width="75" draggable="false"/>
                        <v-img :src="logoText" :width="100" draggable="false"/>
                    </div>
                </router-link>
            </v-card-title>
            <v-card-title v-if="invitation?.user === false" id="heading">Register to the Portal</v-card-title>
            <v-card-title v-else-if="invitation?.company" id="heading">Join <b class="font-italic">{{
                    invitation?.company
                }}</b></v-card-title>
            <v-card-text>
                <v-alert v-if="errors.length" class="mb-3 bg-error" close-label="Close Alert" density="compact"
                         variant="tonal">
                    <p v-for="error in errors">{{ error }}</p>
                </v-alert>

                <!-- Invitation Flow (unchanged) -->
                <template v-if="invitation">
                    <v-btn v-if="invitation?.user" block color="primary" type="submit">Accept Invitation</v-btn>
                    <template v-else>
                        <v-form ref="form" v-model="formData.valid" validate-on="input lazy"
                                @submit.prevent="acceptInvitation">
                            <v-text-field v-model="formData['password']" :rules="[vRules.required, vRules.password]"
                                          autocomplete="new-password" density="compact" label="Password" type="password"/>
                            <v-text-field v-model="formData['password_confirmation']" :rules="[vRules.required]"
                                          autocomplete="new-password" density="compact" label="Confirm Password" type="password"/>
                            <v-btn :disabled="!formData.valid" block color="primary" type="submit">Register</v-btn>
                        </v-form>
                    </template>
                </template>

                <!-- Self-Service Registration Flow (3 steps) -->
                <template v-else>
                    <!-- Registration Complete - Account Being Processed -->
                    <template v-if="pendingApproval">
                        <div class="text-center py-6">
                            <div class="verify-icon-circle mx-auto mb-4" style="background: rgba(76,175,80,0.1)">
                                <v-icon size="36" color="success">mdi-check-circle-outline</v-icon>
                            </div>
                            <h3 class="text-h5 font-weight-bold mb-2">You're Almost There!</h3>
                            <p class="text-body-1 text-grey-darken-2 mb-2">
                                Thank you for registering with SimpliGPS.
                            </p>
                            <p class="text-body-2 text-grey-darken-1 mb-4">
                                Your account is being set up and you'll be ready to go shortly.
                                We'll send a confirmation email to
                                <strong>{{ personalData.email }}</strong> once your account is active.
                            </p>
                            <v-card variant="tonal" color="primary" class="text-left mb-6 pa-4">
                                <div class="d-flex align-start">
                                    <v-icon class="mr-3 mt-1" size="20">mdi-email-check-outline</v-icon>
                                    <div>
                                        <div class="text-body-2 font-weight-medium mb-1">Please check your email</div>
                                        <div class="text-body-2">
                                            You'll receive an email at <strong>{{ personalData.email }}</strong>
                                            when your account is ready to use.
                                            In the meantime, download the <strong>SimpliGPS Fleet</strong> app
                                            so you're all set to get started.
                                        </div>
                                    </div>
                                </div>
                            </v-card>
                            <v-btn block color="primary" @click="navigateTo('login')">
                                Go to Sign In
                            </v-btn>
                        </div>
                    </template>

                    <template v-else>
                    <!-- Step Indicator -->
                    <step-indicator :current-step="displayStep" :labels="['Account', 'Verify', 'Business']"/>

                    <v-divider class="my-3"/>

                    <div class="step-content" style="max-height: 60vh; overflow-y: auto">
                        <!-- STEP 1: Account Info -->
                        <template v-if="currentStep === 1">
                            <v-form v-model="personalData.valid" validate-on="input lazy" @submit.prevent="submitDetails">
                                <v-text-field v-model="personalData['first_name']" :rules="[vRules.required]"
                                              color="primary" density="compact" label="First Name" autofocus/>

                                <v-text-field v-model="personalData['last_name']" :rules="[vRules.required]"
                                              color="primary" density="compact" label="Last Name"/>

                                <v-text-field v-model="personalData['email']" :rules="[vRules.required, vRules.email]"
                                              color="primary" density="compact" label="Email" type="email"/>

                                <v-sheet v-if="phoneAlreadyVerified" border class="d-flex align-center px-3 py-2 mb-4 rounded"
                                         color="surface">
                                    <v-icon class="me-2" color="success" icon="mdi-shield-check" size="small"/>
                                    <div class="flex-grow-1 min-w-0">
                                        <div class="text-caption text-medium-emphasis">Phone</div>
                                        <div class="text-body-2 text-truncate">{{ personalData['contact_phone'] }}</div>
                                    </div>
                                    <span class="text-caption text-success font-weight-medium">Verified</span>
                                </v-sheet>
                                <v-text-field v-else v-model="personalData['contact_phone']" :rules="[vRules.required]"
                                              density="compact" label="Phone" type="tel"
                                              placeholder="e.g. +1 (555) 123-4567"/>

                                <v-text-field v-model="personalData['password']"
                                              :rules="[vRules.required, vRules.password]"
                                              autocomplete="new-password" density="compact" label="Password" type="password"/>

                                <v-text-field v-model="personalData['password_confirmation']"
                                              :rules="[vRules.required, vRules.sameAs(personalData['password'], 'Password')]"
                                              autocomplete="new-password" density="compact"
                                              label="Confirm Password" type="password"/>

                                <!-- Address Section -->
                                <div class="text-body-2 font-weight-bold mt-3 mb-2">Address</div>
                                <v-text-field v-model="personalData['addr_1']" :rules="[vRules.required]"
                                              color="primary" density="compact" label="Address"/>
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_city']" :rules="[vRules.required]"
                                                      density="compact" label="City"/>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_district']"
                                                      density="compact" label="District"/>
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="6">
                                        <v-autocomplete v-if="hasStates(personalData['addr_country'])"
                                                        v-model="personalData['addr_state']"
                                                        :items="statesFor(personalData['addr_country'])"
                                                        :rules="[vRules.required]" density="compact"
                                                        label="State / Province"/>
                                        <v-text-field v-else v-model="personalData['addr_state']"
                                                      :rules="[vRules.required]" density="compact"
                                                      label="State / Province"/>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field v-model="personalData['addr_postalcode']" :rules="[vRules.required]"
                                                      density="compact" label="Postal Code"/>
                                    </v-col>
                                </v-row>
                                <v-autocomplete v-model="personalData['addr_country']" :items="countries"
                                               :rules="[vRules.required]" density="compact" label="Country"
                                               @update:model-value="personalData['addr_state'] = null"/>

                                <v-btn :disabled="!personalData.valid || processing || submitCooldown > 0"
                                       :loading="processing" block color="primary"
                                       class="mt-2" type="submit">
                                    <template v-if="submitCooldown > 0">Try again in {{ submitCooldown }}s</template>
                                    <template v-else>Next</template>
                                </v-btn>
                            </v-form>
                        </template>

                        <!-- STEP 2: Email Verification -->
                        <template v-else-if="currentStep === 2">
                            <div class="text-center mb-4">
                                <div class="verify-icon-circle mx-auto mb-3">
                                    <v-icon size="36" color="primary">mdi-email-outline</v-icon>
                                </div>
                                <h3 class="text-h6 font-weight-bold mb-1">Verify your email</h3>
                                <p class="text-body-2 text-grey">
                                    We sent a verification code to <strong>{{ personalData.email }}</strong>
                                </p>
                            </div>

                            <v-form @submit.prevent="submitDetails">
                                <v-text-field v-model="personalData['code']" label="Verification Code"
                                              density="compact" color="primary" class="mb-2"
                                              placeholder="Enter code from your email"/>

                                <v-btn :disabled="processing || submitCooldown > 0"
                                       :loading="processing" block color="primary" class="mb-3"
                                       @click="submitDetails">
                                    <template v-if="submitCooldown > 0">Try again in {{ submitCooldown }}s</template>
                                    <template v-else>Verify</template>
                                </v-btn>

                                <div class="text-center">
                                    <span v-if="resendCooldown > 0" class="text-caption text-grey">
                                        Resend code in {{ resendCooldown }}s
                                    </span>
                                    <a v-else class="text-caption text-primary text-decoration-none font-weight-bold cursor-pointer"
                                       @click="sendVerificationCode">
                                        Resend Verification Code
                                    </a>
                                </div>
                            </v-form>
                        </template>

                        <!-- STEP 3: Company Setup -->
                        <template v-else-if="currentStep === 3">
                            <!-- Two-card choice -->
                            <template v-if="!companyChoice">
                                <h3 class="text-h6 font-weight-bold text-center mb-4">Set up your business</h3>

                                <v-card class="company-choice-card mb-3 cursor-pointer" variant="outlined"
                                        :color="'primary'" @click="companyChoice = 'join'">
                                    <v-card-text class="d-flex align-center pa-4">
                                        <div class="choice-icon-circle mr-3">
                                            <v-icon size="28" color="primary">mdi-account-group</v-icon>
                                        </div>
                                        <div>
                                            <div class="font-weight-bold text-body-1">Join Existing Organization</div>
                                            <div class="text-caption text-grey">Use an account number to join an existing organization</div>
                                        </div>
                                        <v-spacer/>
                                        <v-icon color="grey">mdi-chevron-right</v-icon>
                                    </v-card-text>
                                </v-card>

                                <v-card class="company-choice-card cursor-pointer" variant="outlined"
                                        :color="'secondary'" @click="companyChoice = 'create'">
                                    <v-card-text class="d-flex align-center pa-4">
                                        <div class="choice-icon-circle mr-3">
                                            <v-icon size="28" color="primary">mdi-domain-plus</v-icon>
                                        </div>
                                        <div>
                                            <div class="font-weight-bold text-body-1">Create New Business</div>
                                            <div class="text-caption text-grey">Set up a new business and invite team members</div>
                                        </div>
                                        <v-spacer/>
                                        <v-icon color="grey">mdi-chevron-right</v-icon>
                                    </v-card-text>
                                </v-card>
                            </template>

                            <!-- Join Existing Company Form -->
                            <template v-else-if="companyChoice === 'join'">
                                <div class="d-flex align-center mb-3">
                                    <v-btn icon size="small" variant="text" @click="companyChoice = null">
                                        <v-icon>mdi-arrow-left</v-icon>
                                    </v-btn>
                                    <h3 class="text-h6 font-weight-bold ms-1">Join Existing Organization</h3>
                                </div>

                                <v-form @submit.prevent="joinCompany">
                                    <v-text-field v-model="companyData['company_code']"
                                                  :rules="[vRules.required, vRules.maxLength(8), vRules.numeric]"
                                                  density="compact" label="Business Account Number"
                                                  prepend-inner-icon="mdi-domain" color="primary"
                                                  autofocus/>

                                    <v-btn :disabled="processing || submitCooldown > 0"
                                           :loading="processing" block color="primary" class="mt-2"
                                           @click="joinCompany">
                                        <template v-if="submitCooldown > 0">Try again in {{ submitCooldown }}s</template>
                                        <template v-else>Join Organization</template>
                                    </v-btn>

                                    <p class="text-center text-caption mt-3">
                                        <a class="text-primary text-decoration-none cursor-pointer"
                                           @click="companyChoice = 'create'">
                                            Or create a new business instead
                                        </a>
                                    </p>
                                </v-form>
                            </template>

                            <!-- Create New Company Form -->
                            <template v-else-if="companyChoice === 'create'">
                                <div class="d-flex align-center mb-3">
                                    <v-btn icon size="small" variant="text" @click="companyChoice = null">
                                        <v-icon>mdi-arrow-left</v-icon>
                                    </v-btn>
                                    <h3 class="text-h6 font-weight-bold ms-1">Create New Business</h3>
                                </div>

                                <v-form v-model="companyData.valid" validate-on="input lazy"
                                        @submit.prevent="createCompany">
                                    <!-- Logo Upload -->
                                    <div class="d-flex align-center mb-3">
                                        <v-avatar :image="image" size="60"/>
                                        <div class="ms-3">
                                            <v-btn size="small" variant="tonal" color="primary"
                                                   @click="$refs.imageHandle.click()">
                                                Upload Logo
                                            </v-btn>
                                            <v-file-input ref="imageHandle" v-model="companyData['file']"
                                                          accept="image/*" class="d-none"
                                                          @change="()=>{ validateFileSize()?imagePreview():''}"/>
                                            <div class="text-caption text-grey mt-1">Optional, max 2MB</div>
                                        </div>
                                    </div>

                                    <!-- Company Details Section -->
                                    <div class="section-card mb-3 pa-3">
                                        <div class="text-body-2 font-weight-bold mb-2">Business Information</div>
                                        <v-text-field v-model="companyData['company_name']"
                                                      :rules="[vRules.required, vRules.maxLength(50)]"
                                                      density="compact" label="Legal Business Name" autofocus/>
                                        <v-text-field v-model="companyData['short_name']"
                                                      :rules="[vRules.required, vRules.maxLength(32)]"
                                                      density="compact" label="Short Name"
                                                      hint="A short version used in reports and dashboards"
                                                      persistent-hint/>
                                        <v-text-field v-model="companyData['phone']" :rules="[vRules.required]"
                                                      density="compact" label="Business Phone" type="tel"/>
                                        <v-select v-model="companyData['industry']"
                                                  :items="industryOptions" :rules="[vRules.required]"
                                                  density="compact" label="Industry"/>
                                        <v-text-field v-if="companyData['industry'] === 'Other'"
                                                      v-model="companyData['industry_other']"
                                                      :rules="[vRules.required]"
                                                      density="compact" label="Specify Industry"/>
                                        <v-select v-model="companyData['planned_asset_count']"
                                                  :items="plannedAssetOptions" :rules="[vRules.required]"
                                                  density="compact" label="Planned Number of Assets"/>
                                    </div>

                                    <!-- Company Address Section -->
                                    <div class="section-card mb-3 pa-3">
                                        <div class="text-body-2 font-weight-bold mb-2">Business Address</div>
                                        <v-text-field v-model="companyData['addr_1']" :rules="[vRules.required]"
                                                      color="primary" density="compact" label="Street Address"/>
                                        <v-row dense>
                                            <v-col cols="6">
                                                <v-text-field v-model="companyData['addr_city']" :rules="[vRules.required]"
                                                              density="compact" label="City"/>
                                            </v-col>
                                            <v-col cols="6">
                                                <v-text-field v-model="companyData['addr_district']" :rules="[vRules.required]"
                                                              density="compact" label="County/District"/>
                                            </v-col>
                                        </v-row>
                                        <v-row dense>
                                            <v-col cols="6">
                                                <v-autocomplete v-if="hasStates(companyData['addr_country'])"
                                                                v-model="companyData['addr_state']"
                                                                :items="statesFor(companyData['addr_country'])"
                                                                :rules="[vRules.required]" density="compact"
                                                                label="State / Province"/>
                                                <v-text-field v-else v-model="companyData['addr_state']"
                                                              :rules="[vRules.required]" density="compact"
                                                              label="State / Province"/>
                                            </v-col>
                                            <v-col cols="6">
                                                <v-text-field v-model="companyData['addr_postalcode']" :rules="[vRules.required]"
                                                              density="compact" label="Zip/Postal Code"/>
                                            </v-col>
                                        </v-row>
                                        <v-autocomplete v-model="companyData['addr_country']" :items="countries"
                                                        :rules="[vRules.required]" density="compact" label="Country"
                                                        @update:model-value="companyData['addr_state'] = null"/>
                                    </div>

                                    <v-btn :disabled="!companyData.valid || processing || submitCooldown > 0"
                                           :loading="processing" block color="primary" type="submit">
                                        <template v-if="submitCooldown > 0">Try again in {{ submitCooldown }}s</template>
                                        <template v-else>Complete Registration</template>
                                    </v-btn>

                                    <p class="text-center text-caption mt-3">
                                        <a class="text-primary text-decoration-none cursor-pointer"
                                           @click="companyChoice = 'join'">
                                            Or join an existing organization instead
                                        </a>
                                    </p>
                                </v-form>
                            </template>
                        </template>
                    </div>

                    <p class="my-3 text-center">Already a member?
                        <a class="text-decoration-none font-weight-bold cursor-pointer"
                           @click="navigateTo('login')">Login</a>
                    </p>
                    </template>
                </template>
            </v-card-text>
        </v-card>
        </div>
    </div>
</template>

<script setup>
import {useRouter} from "vue-router";
import {computed, onBeforeUnmount, onMounted, reactive, ref} from 'vue'
import {send} from '@/composables/apiClient'
import {apiErrorMessage} from '@/composables/useApiError'
import {useAuthStore} from '@/stores/authStore'
import {useOnboardingStore} from '@/stores/onboardingStore'
import logo from "@/assets/images/logo-1.png"
import logoText from "@/assets/images/logo-text.png"
import {useNavigation} from '@/composables/globals'
import {useSnackbarStore} from '@/stores/snackBarStore'
import imagePlaceholder from "@/assets/images/placeholder.png"
import countries from "@/assets/js/countries.json"
import {hasStates, statesFor} from "@/assets/js/stateProvinces"
import StepIndicator from "@/components/auth/StepIndicator.vue"


const router = useRouter()
const user = ref(null)
// Ownership proof for the company step, issued when the emailed code is
// verified. Without it that endpoint has to trust a user_id from the body.
const setupToken = ref(null)
// The number was confirmed by OTP on the previous page and arrives as a
// query param. Show it rather than ask for it again, and do not let it be
// edited into something the server never verified.
const phoneAlreadyVerified = computed(() => {
    const q = router.currentRoute.value?.query ?? {};
    return !!(q.phone && q.verification_token);
});
const errors = ref([])
const invitation = ref(null)
const processing = ref(false)
const invitationToken = ref(router.currentRoute.value?.params?.token ?? null)
const {navigateTo} = useNavigation()
const snackbar = useSnackbarStore()
const currentStep = ref(1)
const companyChoice = ref(null) // null, 'join', 'create'
const resendCooldown = ref(0)
let resendTimer = null
// Throttle-aware local cooldown for the step submit buttons. Server
// returns HTTP 429 + Retry-After when registration endpoints hit their
// rate limit (IP-based, windowed at 10 min). Without this, a 429
// response left the Submit button enabled and impatient re-taps just
// compounded the server counter without the user knowing how long to
// wait.
const submitCooldown = ref(0)
let submitCooldownTimer = null
const startSubmitCooldown = (seconds) => {
    submitCooldown.value = Math.max(1, Math.min(600, Math.floor(seconds || 60)))
    if (submitCooldownTimer) clearInterval(submitCooldownTimer)
    submitCooldownTimer = setInterval(() => {
        submitCooldown.value--
        if (submitCooldown.value <= 0) clearInterval(submitCooldownTimer)
    }, 1000)
}
const handleAuthError = (errorResponse, fallback) => {
    const info = apiErrorMessage(errorResponse, fallback)
    if (info.isThrottled) {
        startSubmitCooldown(info.retryAfterSec || 60)
        errors.value.push(info.message)
        return true
    }
    return false
}
const form = ref(null)
const auth = useAuthStore()
const onboarding = useOnboardingStore()
const image = ref(imagePlaceholder)

// Map internal step to display step for the indicator
const displayStep = computed(() => {
    // currentStep 1 = Account (display 1), 2 = Verify (display 2), 3 = Business (display 3)
    return currentStep.value;
});

const formData = reactive({
    valid: false,
    password: null,
    password_confirmation: null,
})
const personalData = reactive({
    valid: false,
    p: 'commercial',
    email: null,
    // Pre-fill from the verified phone query param as the initial
    // reactive value (not an onMounted assignment). Setting it in
    // onMounted after the form has already rendered means Vuetify's
    // validate-on="input lazy" never fires for this field and the
    // form's `valid` flag stays false even though the field value
    // passes `required`. Seeding the default here lets Vuetify mark
    // the field valid on first render.
    contact_phone: router.currentRoute.value?.query?.phone ?? null,
    first_name: null,
    last_name: null,
    password: null,
    password_confirmation: null,

    addr_1: null,
    addr_district: null,
    addr_city: null,
    addr_state: null,
    addr_postalcode: null,
    addr_country: 'United States',
})
const industryOptions = [
    'Shipping', 'Transportation', 'Vehicle Rental', 'Construction', 'Mining',
    'Agriculture', 'Fleet Management', 'Field Service', 'Logistics', 'Other'
]
const plannedAssetOptions = ['1-10', '11-50', '51-100', '100+']
const pendingApproval = ref(false)

const companyData = reactive({
    valid: false,
    have_company_code: false,
    company_code: null,
    company_name: null,
    short_name: null,
    contact_name: null,
    phone: null,
    addr_1: null,
    addr_district: null,
    addr_city: null,
    addr_state: null,
    addr_postalcode: null,
    addr_country: 'United States',
    industry: null,
    industry_other: null,
    planned_asset_count: null,
})

onMounted(() => {
    // Invitation flow and phone-first flow are mutually exclusive entry
    // points. If an invitation token is present in the URL path, we
    // IGNORE any phone/verification_token query params - those belong
    // to the self-service signup path. Reverse is also true. Guarding
    // against URL-tampering (user visiting /register/INVITE?phone=X)
    // that would otherwise splice both flows into one payload.
    if (invitationToken.value) {
        fetchInvitation();
        return;
    }

    // Pre-fill of contact_phone happens in the `personalData` reactive
    // default (above) so Vuetify can validate the pre-filled value on
    // first render. Setting it here (after mount) used to leave the
    // form's `valid` flag stuck on false - the field passed `required`
    // but `validate-on="input lazy"` never fired because the user
    // never typed in it. The verification_token query param is still
    // consumed later in registerUser() to forward to the server.
})

onBeforeUnmount(() => {
    if (resendTimer) clearInterval(resendTimer);
    if (submitCooldownTimer) clearInterval(submitCooldownTimer);
})

const startResendCooldown = (seconds = 60) => {
    resendCooldown.value = Math.max(1, Math.floor(seconds));
    if (resendTimer) clearInterval(resendTimer);
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0) clearInterval(resendTimer);
    }, 1000);
}

const validateFileSize = () => {
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (companyData['file']?.size > maxSize) {
        snackbar.showSnackbar("File size exceeds 2MB. Please upload a smaller file.", 'error');
        return false;
    }
    return true;
}
const imagePreview = () => {
    image.value = URL.createObjectURL(companyData['file']);
}
const fetchInvitation = () => {
    send('get', `get-invitation/${invitationToken.value}`, null)
        .then((response) => {
            invitation.value = response.data.data
        })
        .catch((errorResponse) => {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                snackbar.showSnackbar('Something went wrong', 'error');
            }
        })
}
const acceptInvitation = () => {
    errors.value = [];
    send('post', `accept-invitation/${invitationToken.value}`, formData)
        .then((response) => {
            if (response.data?.data?.token) {
                auth.login(response.data.data)
                user.email_verified_at === null ? navigateTo('verify-email') : router.go('dashboard')
            } else {
                snackbar.showSnackbar('Company joined successfully, Please login!');
                navigateTo('login');
            }
        })
        .catch((errorResponse) => {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                snackbar.showSnackbar('Something went wrong', 'error');
            }
        })
}

const getClientMetadata = () => {
    const ua = navigator.userAgent;
    let browserName = 'Unknown', browserVersion = '';
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
        browserName = 'Chrome';
        browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] || '';
    } else if (ua.includes('Edg')) {
        browserName = 'Edge';
        browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] || '';
    } else if (ua.includes('Firefox')) {
        browserName = 'Firefox';
        browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] || '';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
        browserName = 'Safari';
        browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] || '';
    }
    let osName = 'Unknown', osVersion = '';
    if (ua.includes('Windows')) { osName = 'Windows'; osVersion = ua.match(/Windows NT ([\d.]+)/)?.[1] || ''; }
    else if (ua.includes('Mac OS')) { osName = 'macOS'; osVersion = ua.match(/Mac OS X ([\d_.]+)/)?.[1]?.replace(/_/g, '.') || ''; }
    else if (ua.includes('Linux')) { osName = 'Linux'; }
    else if (ua.includes('Android')) { osName = 'Android'; osVersion = ua.match(/Android ([\d.]+)/)?.[1] || ''; }
    else if (ua.includes('iPhone') || ua.includes('iPad')) { osName = 'iOS'; osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''; }
    return {
        _browser_name: browserName,
        _browser_version: browserVersion,
        _os_name: osName,
        _os_version: osVersion,
        _screen_resolution: `${screen.width}x${screen.height}`,
    };
};

const submitDetails = () => {
    switch (currentStep.value) {
        case 1:
            registerUser()
            break
        case 2:
            verifyEmail()
            break
    }
}

const joinCompany = () => {
    companyData['have_company_code'] = true;
    registerCompany();
}

const createCompany = () => {
    companyData['have_company_code'] = false;
    registerCompany();
}

const registerUser = () => {
    errors.value = [];
    if (personalData['password'] !== personalData['password_confirmation']) {
        errors.value.push("Password & Confirm Password mismatched");
        return
    }
    processing.value = true;
    // If the user arrived here from the phone-OTP step, pass the
    // verified phone + token through so the server records
    // phone_verified_at on the new user. These query params are set by
    // PhoneOtp.vue on successful verify-otp. Empty on invitation-token
    // flow or legacy entry - the backend treats them as nullable.
    const query = router.currentRoute.value?.query ?? {};
    const phoneVerificationPayload = {};
    if (query.verification_token && query.phone) {
        phoneVerificationPayload.verification_token = query.verification_token;
        phoneVerificationPayload.mobile_phone = query.phone;
    }
    const payload = {...personalData, ...phoneVerificationPayload, ...getClientMetadata()};
    send('post', `register-commercial-user`, payload)
        .then((response) => {
            if (response.data.success) {
                user.value = response.data.data?.user ?? null;
                personalData['addr_1'] = user.value?.addr_1 ?? personalData['addr_1'];
                personalData['addr_district'] = user.value?.addr_district ?? personalData['addr_district'];
                personalData['addr_city'] = user.value?.addr_city ?? personalData['addr_city'];
                personalData['addr_state'] = user.value?.addr_state ?? personalData['addr_state'];
                personalData['addr_postalcode'] = user.value?.addr_postalcode ?? personalData['addr_postalcode'];
                personalData['addr_country'] = user.value?.addr_country ?? personalData['addr_country'];

                if (user.value && !user.value.email_verified_at) {
                    currentStep.value = 2;
                    startResendCooldown();
                } else {
                    currentStep.value = 3;
                }
            }
        })
        .catch((errorResponse) => {
            if (handleAuthError(errorResponse, 'Something went wrong')) return;
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                snackbar.showSnackbar('Something went wrong', 'error');
            }
        })
        .finally(() => processing.value = false)
}
const verifyEmail = () => {
    errors.value = [];
    if (!personalData['code']) {
        errors.value.push("Please enter verification code received in your mailbox.");
        return;
    }
    if (!user.value) {
        snackbar.showSnackbar("Something went wrong", 'error')
        window.location.reload();
        return;
    }
    processing.value = true;
    send('put', `verify-user/${user.value.id}`, {code: personalData['code']})
        .then((response) => {
            if (response.data.success) {
                setupToken.value = response.data.data?.setup_token ?? null;
                currentStep.value = 3;
            }
        }).catch((errorResponse) => {
        if (handleAuthError(errorResponse, 'Something went wrong')) return;
        if (errorResponse.response?.data?.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response?.data?.message) {
            errors.value.push(errorResponse.response.data.message);
        } else {
            snackbar.showSnackbar('Something went wrong', 'error');
        }
    }).finally(() => processing.value = false)
}
const sendVerificationCode = () => {
    if (!user.value) {
        snackbar.showSnackbar("Something went wrong", 'error')
        window.location.reload();
        return;
    }
    processing.value = true;
    send('post', `send-verification/${user.value.id}/mail`, null)
        .then(() => {
            snackbar.showSnackbar('Verification code sent!');
            startResendCooldown();
        })
        .catch(function (errorResponse) {
            // Throttle-aware error message. Laravel's throttle middleware
            // sends HTTP 429 + Retry-After header; the generic
            // "Too Many Attempts" body is too scary for a user who clicked
            // once. Surface the actual retry window when we can read it.
            const status = errorResponse?.response?.status;
            if (status === 429) {
                const retryAfterSec = Number(
                    errorResponse.response?.headers?.['retry-after']
                    ?? errorResponse.response?.headers?.['Retry-After']
                    ?? 0,
                );
                const minutes = retryAfterSec > 0 ? Math.ceil(retryAfterSec / 60) : null;
                const msg = minutes
                    ? `Too many code requests. Try again in about ${minutes} minute${minutes > 1 ? 's' : ''}.`
                    : "Too many code requests. Please wait a few minutes and try again.";
                snackbar.showSnackbar(msg, 'error');
                // Start a local cooldown so the button disables visibly
                // instead of letting the user hammer it and compound the
                // throttle. Clamp to a sensible max window.
                startResendCooldown(Math.min(retryAfterSec || 60, 600));
                return;
            }
            const msg = errorResponse?.response?.data?.message
                ?? "Couldn't send the code. Check your connection and try again.";
            snackbar.showSnackbar(msg, 'error');
        }).finally(() => processing.value = false)
}
const autoLogin = () => {
    send('post', 'login', {email: personalData.email, password: personalData.password})
        .then((loginResponse) => {
            if (loginResponse.data.success && loginResponse.data.data?.token) {
                auth.login(loginResponse.data.data);
                // Don't mark setup complete - let the wizard handle it
                // after user activates their first device
                snackbar.showSnackbar('Account created successfully!');
                // Use router.replace so the pre-dashboard URL (which
                // carries `verification_token` + `phone` in the query
                // for the phone-first flow) doesn't stay in browser
                // history. Back-navigation from dashboard would
                // otherwise expose an already-consumed token in the URL.
                router.replace({name: 'dashboard'});
            } else {
                snackbar.showSnackbar('Registration complete! Please login.');
                router.replace({name: 'login'});
            }
        })
        .catch(() => {
            snackbar.showSnackbar('Registration complete! Please login.');
            router.replace({name: 'login'});
        })
        .finally(() => processing.value = false);
}
const registerCompany = () => {
    errors.value = [];
    if (!user.value) {
        snackbar.showSnackbar("Something went wrong", 'error')
        window.location.reload();
        return;
    }
    processing.value = true;
    companyData['user_id'] = user.value.id;
    if (setupToken.value) companyData['setup_token'] = setupToken.value;

    // When joining with company code, populate required address fields from user's personal data
    if (companyData['have_company_code']) {
        companyData['contact_name'] = `${personalData.first_name || ''} ${personalData.last_name || ''}`.trim();
        companyData['phone'] = personalData.contact_phone || '-';
        companyData['addr_1'] = personalData.addr_1 || '-';
        companyData['addr_district'] = personalData.addr_district || '-';
        companyData['addr_city'] = personalData.addr_city || '-';
        companyData['addr_state'] = personalData.addr_state || '-';
        companyData['addr_postalcode'] = personalData.addr_postalcode || '-';
        companyData['addr_country'] = personalData.addr_country || 'United States';
    }

    // Ensure user address fields have placeholders for API validation
    const userData = {...personalData};
    userData.addr_1 = userData.addr_1 || '-';
    userData.addr_district = userData.addr_district || '-';
    userData.addr_city = userData.addr_city || '-';
    userData.addr_state = userData.addr_state || '-';
    userData.addr_postalcode = userData.addr_postalcode || '-';
    userData.addr_country = userData.addr_country || 'United States';
    userData.code = `tempCode${user.value.id}`;

    // If "Other" industry selected, use the custom text
    const payload = {...companyData, user: userData};
    if (payload.industry === 'Other' && payload.industry_other) {
        payload.industry = payload.industry_other;
    }
    delete payload.industry_other;

    Object.assign(payload, getClientMetadata());
    send('post', `register-commercial-company`, payload)
        .then((response) => {
            if (response.data.success) {
                if (response.data.data?.pending_approval === true) {
                    processing.value = false;
                    pendingApproval.value = true;
                    // Save email so login page can pre-fill it
                    localStorage.setItem('registered_email', personalData.email);
                } else {
                    autoLogin();
                }
            }
        })
        .catch((errorResponse) => {
            if (errorResponse.response?.data?.message?.includes('already have a role') ||
                errorResponse.response?.data?.message?.includes('Already a member')) {
                autoLogin();
                return;
            }
            if (handleAuthError(errorResponse, 'Something went wrong')) {
                processing.value = false;
                return;
            }
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            } else {
                snackbar.showSnackbar('Something went wrong', 'error');
            }
            processing.value = false;
        })
}
</script>

<style scoped>
.wrapper {
    height: 100%;
}

.verify-icon-circle {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(85, 26, 139, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.company-choice-card {
    border-radius: 12px !important;
    transition: box-shadow 0.2s, border-color 0.2s;
}

.company-choice-card:hover {
    box-shadow: 0 2px 12px rgba(85, 26, 139, 0.15) !important;
}

.choice-icon-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    border-radius: 50%;
    background: rgba(85, 26, 139, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.section-card {
    border: 1px solid #E0E0E0;
    border-radius: 12px;
    background: #FAFAFA;
}
</style>
